import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { BookOpen, Check, Clock3, Pencil, Share2, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppSidebar } from '@/components/app-sidebar'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { SiteHeader } from '@/components/site-header'

export const Route = createFileRoute('/_authenticated/journal')({
  head: () => ({
    meta: [{ title: 'Journal — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: JournalPage,
})

type Entry = FunctionReturnType<typeof api.journal.listMine>[number]

function formatDate(timestamp: number, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(timestamp)
}

function JournalPage() {
  const { t, i18n } = useTranslation()
  const entries = useQuery(api.journal.listMine)
  const save = useMutation(api.journal.saveMine)
  const remove = useMutation(api.journal.deleteMine)
  const [entryId, setEntryId] = useState<Id<'journalEntries'> | null>(null)
  const [body, setBody] = useState('')
  const [shareToFeed, setShareToFeed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const entryRef = useRef<HTMLTextAreaElement>(null)
  const selected = entries?.find((entry) => entry._id === entryId)

  function startNew() {
    setEntryId(null)
    setBody('')
    setShareToFeed(false)
  }

  function editEntry(entry: Entry) {
    setEntryId(entry._id)
    setBody(entry.body)
    setShareToFeed(entry.sharedAt !== null)
    entryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    entryRef.current?.focus({ preventScroll: true })
  }

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  async function saveEntry() {
    if (!body.trim() || saving) return
    setSaving(true)
    try {
      await save({
        ...(entryId ? { entryId } : {}),
        body,
        shareToFeed,
      })
      startNew()
      toast.success(t('journalUi.saved'))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('journalUi.saveError'))
    } finally {
      setSaving(false)
    }
  }

  async function deleteEntry(id: Id<'journalEntries'>) {
    if (!window.confirm(t('journalUi.deleteConfirm'))) return
    try {
      await remove({ entryId: id })
      if (entryId === id) startNew()
      toast.success(t('journalUi.deleted'))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('journalUi.deleteError'))
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="flex h-svh flex-col overflow-hidden bg-background">
        <MobileScreenHeader title={t('journalUi.title')} showInbox={false} />
        <SiteHeader className="hidden lg:flex" />
        <main className="flex-1 overflow-y-auto pb-28 lg:pb-10">
          <div className="mx-auto grid max-w-5xl gap-8 px-4 py-6 md:grid-cols-[minmax(0,1.3fr)_minmax(260px,0.7fr)] lg:px-8 lg:py-10">
            <section className="min-w-0">
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <div className="mb-2 flex items-center gap-2 text-primary">
                    <BookOpen className="size-5" />
                    <span className="text-xs font-semibold uppercase tracking-[0.16em]">
                      {t('journalUi.privateLabel')}
                    </span>
                  </div>
                  <h1 className="text-3xl font-bold tracking-tight">{t('journalUi.title')}</h1>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
                    {t('journalUi.description')}
                  </p>
                </div>
              </div>

              <div className="rounded-3xl border border-border/70 bg-card p-4 shadow-sm sm:p-6">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <label htmlFor="journal-entry" className="block text-sm font-semibold">
                    {entryId ? t('journalUi.editEntry') : t('journalUi.prompt')}
                  </label>
                  {entryId && (
                    <button
                      type="button"
                      onClick={startNew}
                      className="cursor-pointer text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                </div>
                <textarea
                  ref={entryRef}
                  id="journal-entry"
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  maxLength={10_000}
                  placeholder={t('journalUi.placeholder')}
                  className="min-h-64 w-full resize-y rounded-2xl border border-border bg-background px-4 py-3 text-[15px] leading-7 outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary/60 focus:ring-2 focus:ring-primary/15"
                />
                <div className="mt-2 flex justify-end text-xs tabular-nums text-muted-foreground">
                  {body.length.toLocaleString(i18n.language)} / {shareToFeed ? '500' : '10,000'}
                </div>

                <div className="mt-4 flex flex-col gap-4 rounded-2xl bg-muted/45 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={shareToFeed}
                      onChange={(event) => setShareToFeed(event.target.checked)}
                      className="mt-1 size-4 accent-primary"
                    />
                    <span>
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <Share2 className="size-4 text-primary" />
                        {t('journalUi.shareLabel')}
                      </span>
                      <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                        {t('journalUi.shareDescription')}
                      </span>
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => void saveEntry()}
                    disabled={!body.trim() || saving || (shareToFeed && body.trim().length > 500)}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Check className="size-4" />
                    {saving ? t('journalUi.saving') : t('journalUi.save')}
                  </button>
                </div>
                {shareToFeed && body.trim().length > 500 && (
                  <p className="mt-3 text-xs text-destructive">{t('journalUi.shareLimit')}</p>
                )}
                {selected?.sharedAt && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    {t('journalUi.alreadyShared')}
                  </p>
                )}
              </div>
            </section>

            <aside className="min-w-0">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-bold">{t('journalUi.pastEntries')}</h2>
                <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  {entries?.length ?? 0}
                </span>
              </div>
              {entries === undefined ? (
                <div className="space-y-3" aria-label={t('journalUi.loading')}>
                  {[0, 1, 2].map((item) => (
                    <div key={item} className="h-24 animate-pulse rounded-2xl bg-muted/60" />
                  ))}
                </div>
              ) : entries.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-border px-6 py-10 text-center">
                  <BookOpen className="mx-auto mb-3 size-8 text-muted-foreground/50" />
                  <p className="font-semibold">{t('journalUi.emptyTitle')}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{t('journalUi.emptyBody')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {entries.map((entry) => (
                    <article
                      key={entry._id}
                      className={`group rounded-2xl border p-4 transition-colors ${entry._id === entryId ? 'border-primary/50 bg-primary/[0.04]' : 'border-border/70 bg-card hover:bg-muted/30'}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleExpanded(entry._id)}
                        aria-expanded={expanded.has(entry._id)}
                        className="block w-full cursor-pointer text-left"
                      >
                        <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                          <Clock3 className="size-3.5" />
                          {formatDate(entry.updatedAt, i18n.language)}
                          {entry.sharedAt !== null && (
                            <span className="ml-auto inline-flex items-center gap-1 text-primary">
                              <Share2 className="size-3" /> {t('journalUi.shared')}
                            </span>
                          )}
                        </span>
                        <span className="mt-3 block whitespace-pre-wrap text-sm leading-relaxed text-foreground/85">
                          {entry.body.length > 220 && !expanded.has(entry._id)
                            ? `${entry.body.slice(0, 220)}…`
                            : entry.body}
                        </span>
                      </button>
                      <div className="mt-3 flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => editEntry(entry)}
                          aria-label={t('journalUi.edit')}
                          title={t('journalUi.edit')}
                          className="cursor-pointer rounded-lg p-2 text-muted-foreground opacity-70 transition-colors hover:bg-muted hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100"
                        >
                          <Pencil className="size-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void deleteEntry(entry._id)}
                          aria-label={t('journalUi.deleteEntry')}
                          className="rounded-lg p-2 text-muted-foreground opacity-70 transition-colors hover:bg-destructive/10 hover:text-destructive sm:opacity-0 sm:group-hover:opacity-100"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                {t('journalUi.historyLimit')}
              </p>
            </aside>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}
